// public/v3/src/pages/usermgmt.jsx — per-tenant admin user management (Phase 1 + 2).
// Roster + add (SSO) + edit role + deactivate/reactivate + force-logout + activity.
// Phase 2 adds: Roles sub-tab, role editor modal, role picker in user edit modal.
import React from 'react';
import { ccmApi } from '../lib/api';
import { UserEditModal } from '../components/user-edit-modal';
import { RoleEditModal } from '../components/role-edit-modal';

export function UserMgmtPage({ user }) {
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [modal, setModal] = React.useState(null); // {mode, user} | null
  const [activity, setActivity] = React.useState(null); // {user, entries} | null
  const [notice, setNotice] = React.useState('');
  const [view, setView] = React.useState('people'); // 'people' | 'roles'
  const [roles, setRoles] = React.useState([]);
  const [roleModal, setRoleModal] = React.useState(null); // {mode, role?} | null

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ccmApi.listUsers();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoles = React.useCallback(async () => {
    try {
      setRoles(await ccmApi.listRoles());
    } catch (e) {
      setError(e?.message || 'Failed to load roles.');
    }
  }, []);

  React.useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  React.useEffect(() => {
    if (isAdmin) loadRoles();
  }, [isAdmin, loadRoles]);

  if (!isAdmin) return <div className="usermgmt-page"><p>User management is admin-only.</p></div>;

  async function run(fn, okMsg) {
    setNotice('');
    setError('');
    try {
      await fn();
      setNotice(okMsg);
      await load();
    } catch (e) {
      setError(e?.message || 'Action failed.');
    }
  }

  async function openActivity(u) {
    setError('');
    try {
      const entries = await ccmApi.userActivity(u.id);
      setActivity({ user: u, entries: Array.isArray(entries) ? entries : [] });
    } catch (e) {
      setError(e?.message || 'Failed to load activity.');
    }
  }

  async function handleModalSubmit(body) {
    if (modal.mode === 'add') {
      // SSO-only provisioning: all new users authenticate via Google Workspace.
      await ccmApi.createUser({ ...body, authProvider: 'google' });
    } else {
      await ccmApi.updateUser(modal.user.id, body);
    }
    const okMsg = modal.mode === 'add' ? 'User added.' : 'User updated.';
    setNotice(okMsg);
    setModal(null);
    await load();
  }

  return (
    <div className="usermgmt-page">
      {notice && <div className="usermgmt-ok" role="status">{notice}</div>}
      {error && <div className="usermgmt-error" role="alert">{error}</div>}

      <div className="usermgmt-subtabs" role="tablist">
        <button role="tab" aria-selected={view === 'people'} className={`btn ghost sm ${view === 'people' ? 'active' : ''}`} onClick={() => setView('people')}>People</button>
        <button role="tab" aria-selected={view === 'roles'} className={`btn ghost sm ${view === 'roles' ? 'active' : ''}`} onClick={() => setView('roles')}>Roles</button>
      </div>

      {view === 'people' && (
        <>
          <div className="usermgmt-header">
            <h2 style={{ margin: 0 }}>Team</h2>
            <button className="btn primary" onClick={() => setModal({ mode: 'add', user: null })}>
              Add user
            </button>
          </div>

          {loading ? (
            <p>Loading…</p>
          ) : (
            <table className="usermgmt-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const isSelf = u.id === user?.id;
                  return (
                  <tr key={u.id} className={!u.active ? 'is-inactive' : ''}>
                    <td>{u.displayName}</td>
                    <td>{u.email}</td>
                    <td>{u.role}</td>
                    <td>{u.active ? 'Active' : 'Disabled'}</td>
                    <td>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}</td>
                    <td>
                      <div className="usermgmt-actions">
                        <button className="btn sm" onClick={() => setModal({ mode: 'edit', user: u })}>
                          Edit
                        </button>
                        {u.active ? (
                          <button
                            className="btn sm"
                            disabled={isSelf}
                            title={isSelf ? 'You cannot deactivate your own account — ask another admin.' : undefined}
                            onClick={() => {
                              if (!window.confirm(`Deactivate ${u.displayName || u.email}? They will be signed out immediately.`)) return;
                              run(() => ccmApi.setUserActive(u.id, false), 'User deactivated.');
                            }}>
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className="btn sm"
                            onClick={() => run(() => ccmApi.setUserActive(u.id, true), 'User reactivated.')}>
                            Reactivate
                          </button>
                        )}
                        <button
                          className="btn sm"
                          disabled={isSelf}
                          title={isSelf ? 'This would sign you out — use the regular sign-out instead.' : undefined}
                          onClick={() => {
                            if (!window.confirm(`Force-logout ${u.displayName || u.email}? All active sessions will be revoked.`)) return;
                            run(() => ccmApi.forceLogoutUser(u.id), 'Sessions revoked.');
                          }}>
                          Force logout
                        </button>
                        <button className="btn sm ghost" onClick={() => openActivity(u)}>
                          Activity
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} style={{ color: 'var(--ink-3)', fontStyle: 'italic', padding: '16px 10px' }}>
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}

      {view === 'roles' && (
        <>
          <div className="usermgmt-header">
            <h2 style={{ margin: 0 }}>Roles</h2>
            <button className="btn primary" onClick={() => setRoleModal({ mode: 'add' })}>New role</button>
          </div>
          <table className="usermgmt-table">
            <thead><tr><th>Name</th><th>Base tier</th><th>Features</th><th></th></tr></thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}{r.isLocked && <span className="usermgmt-badge"> built-in</span>}</td>
                  <td>{r.baseTier}</td>
                  <td>{r.baseTier === 'admin' ? 'all' : (r.features || []).join(', ')}</td>
                  <td className="usermgmt-actions">
                    <button className="btn ghost sm" onClick={() => setRoleModal({ mode: 'edit', role: r })}>Edit</button>
                    {!r.isLocked && (
                      <button className="btn ghost sm" onClick={() => {
                        if (!window.confirm(`Delete role "${r.name}"? Users on it must be reassigned first.`)) return;
                        run(() => ccmApi.deleteRole(r.id), 'Role deleted.').then(loadRoles);
                      }}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ color: 'var(--ink-3)', fontStyle: 'italic', padding: '16px 10px' }}>
                    No roles defined.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {modal && (
        <UserEditModal
          mode={modal.mode}
          user={modal.user}
          roles={roles}
          onClose={() => setModal(null)}
          onSubmit={handleModalSubmit}
        />
      )}

      {roleModal && (
        <RoleEditModal
          mode={roleModal.mode}
          role={roleModal.role}
          onClose={() => setRoleModal(null)}
          onSubmit={async (body) => {
            if (roleModal.mode === 'add') await ccmApi.createRole(body);
            else await ccmApi.updateRole(roleModal.role.id, body);
            setNotice('Role saved.');
            await loadRoles();
          }}
        />
      )}

      {activity && (
        <div className="modal-backdrop" onClick={() => setActivity(null)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Activity for ${activity.user.displayName || activity.user.email}`}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}>
            <div className="modal-head">
              <h3>Activity — {activity.user.displayName || activity.user.email}</h3>
            </div>
            <div className="modal-body">
              {activity.entries.length === 0 ? (
                <p style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>No recent activity.</p>
              ) : (
                <ul className="usermgmt-activity">
                  {activity.entries.map((a, i) => (
                    <li key={i}>
                      <span style={{ fontWeight: 600 }}>{a.action}</span>
                      {a.entity ? <> · <span style={{ color: 'var(--ink-2)' }}>{a.entity}</span></> : null}
                      {a.detail ? <> · <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{a.detail}</span></> : null}
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                        {a.ts ? new Date(a.ts).toLocaleString() : ''}
                        {a.ip ? ` · ${a.ip}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-foot">
              <span />
              <button className="btn" onClick={() => setActivity(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
